import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { withdrawalErrorText } from '../api/withdrawals';
import { fmtDayFull } from '../lib/dates';
import type { DeclaredWithdrawal, SaleSubjectKind, WithdrawalAck as WithdrawalAckData } from '../types';
import { Button, Callout, TextField } from './charte';

// « Renoncer au contrat ici » (vente à distance, T17) : formulaire partagé par la page publique du lien de l'e-mail
// (`start="intro"`) et la page « Conditions de vente » de l'espace élève (T14, 26/10/2026 ; avant, « Mes documents » ;
// `start="fields"`, dans un panneau ouvert par le même bouton : l'élève n'appuie pas deux fois sur le même libellé).
// Libellés imposés : « Renoncer au contrat ici », « Confirmer la rétractation ».
// L'accusé montre les valeurs ENREGISTRÉES rendues par le serveur, jamais celles du formulaire.

const WORDS: Record<SaleSubjectKind, { title: string; ce: string }> = {
  contract: { title: 'Contrat', ce: 'ce contrat' },
  quote: { title: 'Devis', ce: 'ce devis' },
  shop_purchase: { title: 'Achat en ligne', ce: 'cet achat' },
};

/**
 * Mots du document vendu : « Contrat » / « ce contrat », « Devis » / « ce devis », « Achat en ligne » / « cet achat »
 * (« ce » devient « cet » devant la voyelle).
 */
export function subjectWords(kind: SaleSubjectKind): { title: string; ce: string } {
  return WORDS[kind] ?? WORDS.contract;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Message sous chaque champ ; un nom d'une lettre est refusé par le serveur (2 à 200 caractères). */
function issuesOf(values: DeclaredWithdrawal): Record<keyof DeclaredWithdrawal, string | undefined> {
  return {
    name: values.name.trim().length < 2 ? 'Indiquez votre nom.' : undefined,
    contract: values.contract.trim() ? undefined : 'Indiquez le contrat concerné.',
    email: EMAIL_PATTERN.test(values.email.trim()) ? undefined : 'Adresse e-mail invalide.',
  };
}

interface WithdrawalFormProps {
  kind: SaleSubjectKind;
  number: string;
  /** Dernier jour (inclus) du délai, AAAA-MM-JJ. */
  until: string;
  /** Valeurs proposées par le serveur, modifiables. */
  defaults: DeclaredWithdrawal;
  /** `intro` : phrase et bouton « Renoncer au contrat ici », puis les champs ; `fields` : directement les champs. */
  start: 'intro' | 'fields';
  /** Envoie la rétractation et rend l'accusé enregistré ; un refus est affiché tel que le serveur l'écrit. */
  onConfirm: (declared: DeclaredWithdrawal) => Promise<WithdrawalAckData>;
  /** « Annuler » des champs ; sans lui, retour au premier temps. */
  onCancel?: () => void;
}

export function WithdrawalForm({ kind, number, until, defaults, start, onConfirm, onCancel }: WithdrawalFormProps) {
  const { title, ce } = subjectWords(kind);
  const [stage, setStage] = useState(start);
  const [values, setValues] = useState(defaults);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ack, setAck] = useState<WithdrawalAckData | null>(null);

  if (ack) return <WithdrawalAck ack={ack} />;

  const intro = (
    <p className="text-[15px] leading-relaxed text-body">
      {`Vous pouvez renoncer à ${ce}, sans avoir à vous justifier, jusqu’au ${fmtDayFull(until)} inclus.`}
    </p>
  );
  if (stage === 'intro')
    return (
      <div className="space-y-5">
        {intro}
        <Button variant="primary" className="h-11 max-sm:w-full" onClick={() => setStage('fields')}>
          Renoncer au contrat ici
        </Button>
      </div>
    );

  const issues = issuesOf(values);
  const valid = !issues.name && !issues.contract && !issues.email;
  const field = (key: keyof DeclaredWithdrawal) => ({
    value: values[key],
    error: issues[key],
    required: true,
    onChange: (e: ChangeEvent<HTMLInputElement>) => setValues({ ...values, [key]: e.target.value }),
  });
  const cancel = () => {
    if (onCancel) return onCancel();
    setValues(defaults);
    setError('');
    setStage('intro');
  };
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      setAck(await onConfirm({ name: values.name.trim(), contract: values.contract.trim(), email: values.email.trim() }));
    } catch (err) {
      setError(withdrawalErrorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-5" aria-label={`${title} ${number}`} noValidate onSubmit={(e) => void submit(e)}>
      {intro}
      <div className="grid gap-4">
        <TextField label="Votre nom" autoComplete="name" maxLength={200} {...field('name')} />
        <TextField label="Contrat concerné" maxLength={300} {...field('contract')} />
        <TextField
          label="Adresse e-mail pour l’accusé de réception"
          type="email"
          inputMode="email"
          autoComplete="email"
          maxLength={320}
          {...field('email')}
        />
      </div>
      <p className="text-sm leading-relaxed text-secondary">
        {`En confirmant, vous renoncez à ${ce}. Votre auto-école vous remboursera les sommes versées au plus tard 14 jours après avoir reçu votre rétractation.`}
      </p>
      {error && (
        <div role="alert">
          <Callout tone="warn">{error}</Callout>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-line pt-5">
        <Button type="submit" variant="primary" className="h-11 max-sm:w-full" loading={busy} disabled={!valid}>
          Confirmer la rétractation
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={cancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}

/** Accusé à l'écran : valeurs enregistrées par le serveur (page publique : `ack` ; « Conditions de vente » : `withdrawal`). */
export function WithdrawalAck({ ack }: { ack: WithdrawalAckData }) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  // L'accusé remplace le formulaire : il prend le focus (annoncé, et visible sur téléphone).
  useEffect(() => ref.current?.focus(), []);
  const line = 'min-w-0 break-words font-medium text-heading';
  return (
    <section ref={ref} tabIndex={-1} aria-labelledby={titleId} className="space-y-4 outline-none">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-success-light text-success-dark">
          <CheckCircle2 size={20} aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 id={titleId} className="text-title-sm text-heading">
            Rétractation enregistrée
          </h2>
          <p className="mt-1 text-sm text-body">
            {`Votre rétractation a été reçue le ${fmtDayFull(ack.receivedOn)}${ack.receivedTime ? ` à ${ack.receivedTime}` : ''}.`}
          </p>
        </div>
      </div>
      {ack.declaredEmail && (
        <p className="break-words text-sm text-body">
          {`Un accusé de réception est adressé à ${ack.declaredEmail} ; conservez aussi cette page.`}
        </p>
      )}
      <p className="text-sm text-body">{`Votre auto-école doit vous rembourser au plus tard le ${fmtDayFull(ack.refundBy)}.`}</p>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 rounded-row bg-surface-light p-4 text-sm sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-y-2">
        <dt className="text-secondary">Nom</dt>
        <dd className={line}>{ack.declaredName}</dd>
        <dt className="mt-2 text-secondary sm:mt-0">Contrat</dt>
        <dd className={line}>{ack.declaredContract}</dd>
        {ack.declaredEmail && (
          <>
            <dt className="mt-2 text-secondary sm:mt-0">Adresse de l’accusé</dt>
            <dd className={line}>{ack.declaredEmail}</dd>
          </>
        )}
      </dl>
    </section>
  );
}
